import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type SurveyGroupBrand as SurveyGroupBrandBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type SurveyGroupBrand = App.PickGraphQL<SurveyGroupBrandBase, 'id' | 'name'>

export type SurveyGroupBrandSelectorProps<
  TSurveyGroupBrand extends SurveyGroupBrand,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  brands: TSurveyGroupBrand[]
  onChange?: (brandId: TSurveyGroupBrand['id']) => void
  value: TSurveyGroupBrand['id'] | Unassigned
}

export const SurveyGroupBrandSelector = React.forwardRef(
  <TSurveyGroupBrand extends SurveyGroupBrand>(
    {
      brands,
      onChange,
      value: valueProp,
      ...rest
    }: SurveyGroupBrandSelectorProps<TSurveyGroupBrand>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const value = React.useMemo(
      () =>
        brands.find(({ id }) => id === valueProp) ?? { id: unassignedSymbol },
      [brands, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (brand: TSurveyGroupBrand) => {
        onChange?.(brand.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={brands}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
